<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobSchedulerConfig',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const kwargs = ref(null);
  const formId = ref(null);
  const formName = ref('JobSchedulerConfig');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_220434438163235_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    kwargs: kwargs.value,
    formId: formId.value,
    formName: formName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_220434438163235_options: b_220434438163235_options.value,
  }));

  const $$load_resource = () => {
    return props.oid
      ? $fn.value.fetch('read_job_scheduler_config', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.schemaDefaults('CreateJobSchedulerConfig');
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_kwargs = () => {
    return editor.value.kwargs
      ? JSON.stringify(editor.value.kwargs, null, 2)
      : null;
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'kwargs', dependsOn: ['editor'] },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_220434438163235_options', nodeId: '220434438163235' },
    ],
    loaders: {
      resource: $$load_resource,
      editor: $$load_editor,
      kwargs: $$load_kwargs,
      formId: $$load_formId,
    },
    ux: {},
    dataRefs: {
      resource,
      editor,
      kwargs,
      formId,
      formName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_220434438163235_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: [
        $fn.value.pgettext(
          'JobSchedulerConfig|Title',
          'Job scheduler configuration'
        ),
      ],
      icon: $config.value.settings.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vdkqemuhs_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_0mpzbgfkd_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_xkahuj6pz_getActionArgs = async ($event) => {
    return {
      objectType: 'job_scheduler_config',
      op: 'upsert',
      data: {
        ...editor.value.getData(),
        kwargs: kwargs.value ? JSON.parse(kwargs.value) : null,
      },
    };
  };
  const act_2140198529698299_closeModal_oyjkeyz1x_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_lxryo3pvg_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_job_scheduler_config',
      data: resource.value.getData({ fields: ['id', 'etag'] }),
    };
  };
  const act_2167826738365259_closeModal_v4vex42kt_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_vdkqemuhs =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vdkqemuhs_getActionArgs,
    });
  const inst_evh_7315092382398562_close_nu9i7hnxt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vdkqemuhs,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_nu9i7hnxt],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_fs9adf7ez =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_0m5jh0ld7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fs9adf7ez,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_0m5jh0ld7],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_swrez0o2d =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_y53p4aut5 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_swrez0o2d,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_y53p4aut5],
    event: 'close',
  });
  const act_1040167445267876_closeModal_0mpzbgfkd =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_0mpzbgfkd_getActionArgs,
    });
  const inst_evh_1040167445267876_success_f75frjbi8 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_0mpzbgfkd,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_xkahuj6pz = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_xkahuj6pz_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_f75frjbi8],
  });
  const inst_evh_1040167445267876_submit_ecmb86xj1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_xkahuj6pz,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ecmb86xj1],
    event: 'submit',
  });
  const act_2140198529698299_closeModal_oyjkeyz1x =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_oyjkeyz1x_getActionArgs,
    });
  const inst_evh_2140198529698299_success_ve4aix591 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_oyjkeyz1x,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_lxryo3pvg = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_lxryo3pvg_getActionArgs,
    displayName: 'delete',
    events: [inst_evh_2140198529698299_success_ve4aix591],
  });
  const inst_evh_2140198529698299_clickPreventStop_qsljcjpqd =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_lxryo3pvg,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_qsljcjpqd],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_v4vex42kt =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_v4vex42kt_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_wg8xd8s2u =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_v4vex42kt,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_wg8xd8s2u],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_nu9i7hnxt,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_0m5jh0ld7,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_y53p4aut5,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_f75frjbi8,
    inst_evh_1040167445267876_submit_ecmb86xj1,
    save: inst_evh_1040167445267876_submit_ecmb86xj1,
    inst_evh_1040167445267876_submit,
    inst_evh_2140198529698299_success_ve4aix591,
    inst_evh_2140198529698299_clickPreventStop_qsljcjpqd,
    delete: inst_evh_2140198529698299_clickPreventStop_qsljcjpqd,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_wg8xd8s2u,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_nu9i7hnxt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_nu9i7hnxt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_0m5jh0ld7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_0m5jh0ld7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_y53p4aut5 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_y53p4aut5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_f75frjbi8 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_f75frjbi8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ecmb86xj1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ecmb86xj1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_ve4aix591 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_ve4aix591.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_qsljcjpqd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_qsljcjpqd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_wg8xd8s2u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_wg8xd8s2u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="JobSchedulerConfig"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :readonly="resource.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="name"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Name')"
              required
            >
              <ItpFormInput name="name" v-model="editor.name" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="notes"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Notes')"
            >
              <ItpFormTextarea name="notes" v-model="editor.notes">
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="function_name"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Function')"
              required
            >
              <ItpFormInput
                name="function_name"
                v-model="editor.function_name"
                required
              >
              </ItpFormInput>
              <template #description>
                <ItpAlert
                  show
                  type="warning"
                  class="mt-3"
                  v-if="resource.function_resolve_error"
                >
                  <p>
                    <ItpIcon fa="exclamation-circle"> </ItpIcon>
                    <ItpText wrap>
                      {{ resource.function_resolve_error }}
                    </ItpText>
                  </p>
                </ItpAlert>
              </template>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="crontab_trigger"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Cron Tab Entry')
              "
            >
              <ItpFormInput
                name="crontab_trigger"
                v-model="editor.crontab_trigger"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="3"
              name="jitter"
              :label="pgettext('NAVOnlineInvoiceInterfaceSettings', 'Jitter')"
            >
              <ItpFormInput type="number" name="jitter" v-model="editor.jitter">
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="kwargs"
              :label="
                pgettext('NAVOnlineInvoiceInterfaceSettings', 'Parameters')
              "
            >
              <ItpCodeMirror
                mode="json"
                :options="b_220434438163235_options"
                v-model="kwargs"
                class="border"
              >
              </ItpCodeMirror>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Delete')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.delete.isRunning"
          class="me-auto"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
